import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Linkedin, Github, MessageCircle, Loader2, Send } from "lucide-react";
import { Reveal, SectionHeader } from "@/components/site/Reveal";
import { SOCIALS } from "@/data/content";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
    "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3.5 text-sm text-white placeholder-zinc-500 outline-none transition-colors duration-300 focus:border-cyan-400 focus:bg-white/10 focus-visible:ring-2 focus-visible:ring-cyan-500/40";

const errorClass = "mt-1.5 text-xs text-red-400";

const initialForm = {
    name: "",
    email: "",
    company: "",
    budget: "",
    project_type: "",
    message: "",
};

export const Contact = () => {
    const { t, lang } = useLanguage();
    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [sending, setSending] = useState(false);

    const set = (key) => (e) => {
        setForm({ ...form, [key]: e.target.value });
        if (errors[key]) setErrors({ ...errors, [key]: null });
    };

    const validate = () => {
        const next = {};
        if (form.name.trim().length < 2) next.name = t.contact.errors.name;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = t.contact.errors.email;
        if (form.message.trim().length < 10) next.message = t.contact.errors.message;
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setSending(true);
        try {
            await axios.post(`${API}/contact`, { ...form, lang });
            toast.success(t.contact.toastSuccess);
            setForm(initialForm);
        } catch (err) {
            toast.error(t.contact.toastError);
        } finally {
            setSending(false);
        }
    };

    const socials = [
        { icon: Mail, label: SOCIALS.email, href: `mailto:${SOCIALS.email}`, testid: "contact-email-link" },
        { icon: Linkedin, label: "LinkedIn", href: SOCIALS.linkedin, testid: "contact-linkedin-link" },
        { icon: Github, label: "GitHub", href: SOCIALS.github, testid: "contact-github-link" },
        { icon: MessageCircle, label: "WhatsApp", href: SOCIALS.whatsapp, testid: "contact-whatsapp-link" },
    ];

    return (
        <section id="contact" className="relative py-24 md:py-32 bg-surface/40 overflow-hidden" data-testid="contact-section">
            <div
                className="pointer-events-none absolute -bottom-40 left-1/2 -translate-x-1/2 h-[400px] w-[800px] rounded-full bg-cyan-400/[0.07] blur-[130px]"
                aria-hidden="true"
            />
            <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
                    <div className="lg:col-span-2">
                        <SectionHeader
                            number={t.contact.number}
                            label={t.contact.label}
                            title={t.contact.title}
                            description={t.contact.description}
                        />
                        <Reveal delay={0.25}>
                            <ul className="space-y-3 -mt-6">
                                {socials.map((s) => (
                                    <li key={s.testid}>
                                        <a
                                            href={s.href}
                                            target={s.href.startsWith("http") ? "_blank" : undefined}
                                            rel={s.href.startsWith("http") ? "noopener noreferrer" : undefined}
                                            className="group inline-flex items-center gap-4 rounded-xl border border-white/5 bg-white/[0.02] px-5 py-4 w-full transition-colors duration-300 hover:border-cyan-400/30 hover:bg-white/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
                                            data-testid={s.testid}
                                        >
                                            <s.icon className="h-5 w-5 text-cyan-400" strokeWidth={1.5} />
                                            <span className="font-mono text-sm text-zinc-300 transition-colors duration-300 group-hover:text-white">
                                                {s.label}
                                            </span>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    </div>

                    <Reveal delay={0.15} className="lg:col-span-3">
                        <form
                            onSubmit={submit}
                            noValidate
                            className="glass-card rounded-2xl p-6 md:p-10 glow-shadow"
                            data-testid="contact-form"
                        >
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <div>
                                    <label htmlFor="contact-name" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">
                                        {t.contact.labels.name}
                                    </label>
                                    <input
                                        id="contact-name"
                                        type="text"
                                        value={form.name}
                                        onChange={set("name")}
                                        placeholder={t.contact.placeholders.name}
                                        className={inputClass}
                                        data-testid="contact-name-input"
                                    />
                                    {errors.name && <p className={errorClass} data-testid="contact-name-error">{errors.name}</p>}
                                </div>
                                <div>
                                    <label htmlFor="contact-email" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">
                                        {t.contact.labels.email}
                                    </label>
                                    <input
                                        id="contact-email"
                                        type="email"
                                        value={form.email}
                                        onChange={set("email")}
                                        placeholder={t.contact.placeholders.email}
                                        className={inputClass}
                                        data-testid="contact-email-input"
                                    />
                                    {errors.email && <p className={errorClass} data-testid="contact-email-error">{errors.email}</p>}
                                </div>
                                <div>
                                    <label htmlFor="contact-company" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">
                                        {t.contact.labels.company}
                                    </label>
                                    <input
                                        id="contact-company"
                                        type="text"
                                        value={form.company}
                                        onChange={set("company")}
                                        placeholder={t.contact.placeholders.company}
                                        className={inputClass}
                                        data-testid="contact-company-input"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="contact-budget" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">
                                        {t.contact.labels.budget}
                                    </label>
                                    <select
                                        id="contact-budget"
                                        value={form.budget}
                                        onChange={set("budget")}
                                        className={`${inputClass} appearance-none ${form.budget ? "" : "text-zinc-500"}`}
                                        data-testid="contact-budget-select"
                                    >
                                        <option value="" className="bg-surface">{t.contact.selectBudget}</option>
                                        {t.contact.budgetOptions.map((opt) => (
                                            <option key={opt} value={opt} className="bg-surface">{opt}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="contact-project-type" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">
                                        {t.contact.labels.projectType}
                                    </label>
                                    <select
                                        id="contact-project-type"
                                        value={form.project_type}
                                        onChange={set("project_type")}
                                        className={`${inputClass} appearance-none ${form.project_type ? "" : "text-zinc-500"}`}
                                        data-testid="contact-project-type-select"
                                    >
                                        <option value="" className="bg-surface">{t.contact.selectType}</option>
                                        {t.contact.typeOptions.map((opt) => (
                                            <option key={opt} value={opt} className="bg-surface">{opt}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="contact-message" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">
                                        {t.contact.labels.message}
                                    </label>
                                    <textarea
                                        id="contact-message"
                                        rows={5}
                                        value={form.message}
                                        onChange={set("message")}
                                        placeholder={t.contact.placeholders.message}
                                        className={`${inputClass} resize-none`}
                                        data-testid="contact-message-input"
                                    />
                                    {errors.message && <p className={errorClass} data-testid="contact-message-error">{errors.message}</p>}
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={sending}
                                className="group mt-8 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#00D2FF] to-[#3A7BD5] px-10 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em] text-[#030305] transition-transform duration-300 hover:scale-[1.03] disabled:opacity-60 disabled:hover:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#030305]"
                                data-testid="contact-submit-button"
                            >
                                {sending ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        {t.contact.sending}
                                    </>
                                ) : (
                                    <>
                                        {t.contact.submit}
                                        <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                                    </>
                                )}
                            </button>
                        </form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};
